/**
 * Generate a shareable "My Reader Vibe" image (Instagram Story size 1080x1920).
 * Pure Canvas API — no external dependencies.
 */

const W = 1080;
const H = 1920;

// Preload the emoji font by wrapping in a system emoji stack that iOS/Android/Chrome all resolve.
const EMOJI_FONT = '"Apple Color Emoji","Segoe UI Emoji","Noto Color Emoji","Twemoji Mozilla",sans-serif';

/**
 * Draw the share image onto a canvas and return a Blob (PNG).
 */
export async function buildVibeShareImage({ vibeKey, vibe, firstName, ctaUrl = 'bookishbestie.com' }) {
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');

  // ============ Background ============
  // Deep archetype color base
  const base = vibe.color;
  const g = ctx.createLinearGradient(0, 0, 0, H);
  g.addColorStop(0, darken(base, 0.35));
  g.addColorStop(0.5, base);
  g.addColorStop(1, darken(base, 0.55));
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, W, H);

  // Grain / paper overlay
  addGrain(ctx, W, H, 0.06);

  // ============ Top decoration: brand ribbon ============
  ctx.fillStyle = 'rgba(253,251,247,0.94)';
  roundRect(ctx, W / 2 - 260, 120, 520, 92, 46);
  ctx.fill();
  ctx.fillStyle = '#6B7A4F';
  ctx.font = 'bold 28px "Playfair Display", serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Bookish Bestie', W / 2, 166);
  ctx.fillStyle = '#5C5955';
  ctx.font = '18px "Inter", sans-serif';
  ctx.fillText('Built for Bookish', W / 2, 200);

  // ============ Emoji circle ============
  const emojiCx = W / 2;
  const emojiCy = 420;
  ctx.beginPath();
  ctx.arc(emojiCx, emojiCy, 130, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(253,251,247,0.15)';
  ctx.fill();
  // ring
  ctx.beginPath();
  ctx.arc(emojiCx, emojiCy, 135, 0, Math.PI * 2);
  ctx.strokeStyle = 'rgba(253,251,247,0.7)';
  ctx.lineWidth = 3;
  ctx.stroke();

  ctx.font = `170px ${EMOJI_FONT}`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(vibe.emoji, emojiCx, emojiCy + 12);

  // ============ Personal label ============
  ctx.fillStyle = 'rgba(253,251,247,0.85)';
  ctx.font = 'bold 34px "Inter", sans-serif';
  ctx.textAlign = 'center';
  const introLine = firstName ? `${firstName.toUpperCase()} IS` : 'I AM';
  drawSpacedText(ctx, introLine, W / 2, 620, 5);

  // ============ Archetype name ============
  ctx.fillStyle = '#FDFBF7';
  ctx.font = 'bold 108px "Playfair Display", serif';
  ctx.textAlign = 'center';
  ctx.fillText(`A ${vibe.name}`, W / 2, 730);

  // ============ Tagline ============
  ctx.fillStyle = 'rgba(253,251,247,0.9)';
  ctx.font = 'italic 36px "Playfair Display", serif';
  wrapText(ctx, `"${vibe.tagline}"`, W / 2, 830, W - 200, 48);

  // ============ Picks section ============
  const picksY = 1080;
  ctx.fillStyle = 'rgba(253,251,247,0.75)';
  ctx.font = 'bold 24px "Inter", sans-serif';
  ctx.textAlign = 'center';
  drawSpacedText(ctx, 'MY VIBE READS', W / 2, picksY, 6);

  // three cards
  vibe.picks.forEach((p, i) => {
    const y = picksY + 70 + i * 170;
    // card bg
    ctx.fillStyle = 'rgba(253,251,247,0.13)';
    roundRect(ctx, 100, y, W - 200, 140, 20);
    ctx.fill();
    // number badge
    ctx.beginPath();
    ctx.arc(180, y + 70, 42, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(253,251,247,0.95)';
    ctx.fill();
    ctx.fillStyle = base;
    ctx.font = 'bold 42px "Playfair Display", serif';
    ctx.textAlign = 'center';
    ctx.fillText(String(i + 1), 180, y + 84);
    // title
    ctx.fillStyle = '#FDFBF7';
    ctx.font = 'bold 34px "Playfair Display", serif';
    ctx.textAlign = 'left';
    ctx.fillText(truncate(p.title, 28), 250, y + 60);
    // author
    ctx.fillStyle = 'rgba(253,251,247,0.75)';
    ctx.font = '24px "Inter", sans-serif';
    ctx.fillText(`by ${p.author}`, 250, y + 100);
  });

  // ============ Footer CTA ============
  ctx.fillStyle = '#FDFBF7';
  roundRect(ctx, W / 2 - 350, H - 220, 700, 110, 55);
  ctx.fill();
  ctx.fillStyle = base;
  ctx.font = 'bold 32px "Inter", sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('Find YOUR Reader Vibe', W / 2, H - 175);
  ctx.fillStyle = '#6B7A4F';
  ctx.font = 'bold 30px "Inter", sans-serif';
  ctx.fillText(ctaUrl, W / 2, H - 132);

  ctx.fillStyle = 'rgba(253,251,247,0.6)';
  ctx.font = '22px "Inter", sans-serif';
  ctx.fillText('#BookishBestie · #BookTok', W / 2, H - 60);

  return await new Promise((resolve) =>
    canvas.toBlob((b) => resolve(b), 'image/png', 0.95)
  );
}

// ---------- utilities ----------
function darken(hex, pct) {
  const c = hex.replace('#', '');
  const r = parseInt(c.slice(0, 2), 16);
  const g = parseInt(c.slice(2, 4), 16);
  const b = parseInt(c.slice(4, 6), 16);
  const dr = Math.max(0, Math.round(r * (1 - pct)));
  const dg = Math.max(0, Math.round(g * (1 - pct)));
  const db = Math.max(0, Math.round(b * (1 - pct)));
  return `rgb(${dr},${dg},${db})`;
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}

function addGrain(ctx, w, h, alpha) {
  const imageData = ctx.getImageData(0, 0, w, h);
  const d = imageData.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * 40 * alpha;
    d[i] = Math.min(255, Math.max(0, d[i] + n));
    d[i + 1] = Math.min(255, Math.max(0, d[i + 1] + n));
    d[i + 2] = Math.min(255, Math.max(0, d[i + 2] + n));
  }
  ctx.putImageData(imageData, 0, 0);
}

function drawSpacedText(ctx, text, x, y, spacing) {
  const chars = text.split('');
  const totalWidth =
    chars.reduce((sum, c) => sum + ctx.measureText(c).width, 0) + spacing * (chars.length - 1);
  let cx = x - totalWidth / 2;
  for (const c of chars) {
    ctx.textAlign = 'left';
    ctx.fillText(c, cx, y);
    cx += ctx.measureText(c).width + spacing;
  }
  ctx.textAlign = 'center'; // reset
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight) {
  const words = text.split(' ');
  let line = '';
  const lines = [];
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  lines.forEach((l, i) => ctx.fillText(l, x, y + i * lineHeight));
}

function truncate(str, max) {
  if (str.length <= max) return str;
  return str.slice(0, max - 1) + '…';
}
